<!--
  =====================================================================
  OPERE D'ARTE PAGE - ARTWORKS GALLERY
  =====================================================================
  
  Displays company art collection with image-based sections and
  navigation submenu for smooth scrolling.
  
  FEATURES:
  - Dynamic section rendering for art pieces
  - Auto-generated submenu from section titles
  - Anchor IDs for smooth navigation between artworks
  - Supports center, left, and right image layouts
  - Shows loading and error states
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <PageSubmenu
      v-if="state.pageSections.length"
      mainLabel="Opere d'arte"
      :sections="state.pageSections"
      :initialSection="initialLabel"
      labelKey="title"
      :overrideExisting="true"
    />

    <main class="pagina">
      <div v-if="state.loading">Caricamento...</div>
      
      <div v-else-if="state.error">Errore</div>
      
      <div v-else>
        <component
          v-for="(section, index) in state.pageSections"
          :key="index"
          :is="getComponent(section.type)"
          :id="slugify(section.content?.title || section.content?.eyebrow || '')"
          :content="section.content"
        />
      </div>
    </main>

    <ArrowUp />
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { state, loadPageContent } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'
import PageSubmenu from '@/components/sections/shared/PageSubmenu.vue'

// ───────────────────────────────────────────────────────────────────
// OPERA D'ARTE SPECIFIC COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionCenterImageHtml from '@/components/sections/opere-arte/SectionCenterImageHtml.vue'
import SectionLeftImageHtml from '@/components/sections/opere-arte/SectionLeftImageHtml.vue'
import SectionRightImageHtml from '@/components/sections/opere-arte/SectionRightImageHtml.vue'

// ───────────────────────────────────────────────────────────────────
// CROSS-SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Gets the title of the first section for initial submenu label
 * @returns {string} First section title or eyebrow
 */
const initialLabel = computed(() => {
  const first = state.pageSections?.[0]?.content
  return first?.title || first?.eyebrow || ''
})

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Load artworks data on component mount
 * Extracts sections from page content
 */
onMounted(async () => {
  await loadPageContent('/opere-arte')
  state.pageSections = state.page.sections || []
})

// ───────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ───────────────────────────────────────────────────────────────────

/**
 * Convert text to URL-safe anchor ID
 * @param {string} str - Text to slugify
 * @returns {string} URL-safe slug
 */
function slugify(str = '') {
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Maps section types to their corresponding Vue components
 * 
 * @param {string} type - The section type from page content
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'section-center-image-html': 
      return SectionCenterImageHtml
    case 'section-left-image-html': 
      return SectionLeftImageHtml
    case 'section-right-image-html': 
      return SectionRightImageHtml
    default: 
      return SectionCenterImageHtml
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.dots-loader {
  display: flex;
  align-items: center;
  @include flex-gap(1.5rem);
  .dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    animation: dot-pulse 0.9s ease-in-out infinite;
    &.dot-1 { background-color: #c9d745; animation-delay: 0s; }
    &.dot-2 { background-color: #8bbb58; animation-delay: 0.2s; }
    &.dot-3 { background-color: #629933; animation-delay: 0.4s; }
  }
}
@keyframes dot-pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.7); opacity: 1; }
}

.pagina {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  @include flex-gap(4rem, column);
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: $space-4; // Spazio tra le diverse opere
  }

  @media (max-width: 470px) {
    margin-top: 0.5rem;
  }
}
</style>